"use strict";
/**
 * SoundboardService.ts
 * High-performance Web Audio API Soundboard for Meci Voice Channels.
 * Provides synthesized iconic SFX (Airhorn, Ba-Dum-Tss, Quack, GG, Bruh, Victory, Applause, etc.)
 * plus custom sound upload and real-time broadcasting to all channel participants.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.soundboardService = exports.DEFAULT_SOUNDS = void 0;
const RealtimeService_1 = require("./RealtimeService");
const useStore_1 = require("../store/useStore");
exports.DEFAULT_SOUNDS = [
    { id: 'airhorn', name: 'Airhorn', emoji: '📯', category: 'classics', durationMs: 1200 },
    { id: 'badumtss', name: 'Ba-Dum Tss', emoji: '🥁', category: 'classics', durationMs: 1400 },
    { id: 'quack', name: 'Vak Vak', emoji: '🦆', category: 'memes', durationMs: 800 },
    { id: 'bruh', name: 'Bruh', emoji: '🗿', category: 'memes', durationMs: 900 },
    { id: 'gg', name: 'GG (Good Game)', emoji: '🏆', category: 'gaming', durationMs: 1100 },
    { id: 'victory', name: 'Zafer Fanfarı', emoji: '🎺', category: 'gaming', durationMs: 1800 },
    { id: 'tada', name: 'Ta-Daaa!', emoji: '🎉', category: 'classics', durationMs: 1300 },
    { id: 'applause', name: 'Coşkulu Alkış', emoji: '👏', category: 'classics', durationMs: 2200 },
    { id: 'boing', name: 'Karikatür Yay', emoji: '🌀', category: 'memes', durationMs: 900 },
    { id: 'discord_ping', name: 'Discord Ping', emoji: '🔔', category: 'memes', durationMs: 600 },
    { id: 'rimshot', name: 'Rimshot', emoji: '🎭', category: 'classics', durationMs: 1000 },
    { id: 'fart', name: 'Osuruk Sesi', emoji: '💨', category: 'memes', durationMs: 1100 },
];
class SoundboardService {
    constructor() {
        this.audioCtx = null;
        this.volume = 0.8; // 0.0 to 1.0
        this.reactionListeners = new Set();
        this.customSounds = [];
        this.currentAudioElement = null;
        if (typeof window !== 'undefined') {
            const savedVol = localStorage.getItem('meci_soundboard_vol');
            if (savedVol)
                this.volume = Math.max(0, Math.min(1, Number(savedVol) / 100));
            this.loadCustomSounds();
            // Listen for incoming soundboard broadcasts from socket
            try {
                const socket = RealtimeService_1.realtimeService.getSocket();
                socket.on('voice:soundboard', (data) => {
                    if (!data)
                        return;
                    // Action commands: stop / pause / resume
                    if (data.action === 'stop' || data.action === 'pause') {
                        this.stopAudio();
                        return;
                    }
                    if (data.action === 'resume' && this.currentAudioElement) {
                        this.currentAudioElement.play().catch(() => { });
                        return;
                    }
                    // Skip music streams — they are handled by music:play in realtimeSync.ts
                    if (data.audioUrl || data.url) {
                        // Music streams are now exclusively handled by realtimeSync music:play
                        // Only trigger the visual reaction badge
                        this.notifyReaction({
                            soundId: data.soundId || 'music',
                            soundName: data.soundName || 'Müzik',
                            emoji: data.emoji || '🎵',
                            userId: String(data.userId || ''),
                            userName: data.userName || 'Kullanıcı',
                            timestamp: Date.now(),
                        });
                        return;
                    }
                    // Built-in synthesized SFX only
                    if (data.soundId) {
                        this.playSynthesizedSound(data.soundId);
                    }
                    // Trigger visual reaction badge
                    this.notifyReaction({
                        soundId: data.soundId || 'music',
                        soundName: data.soundName || 'Müzik',
                        emoji: data.emoji || '🎵',
                        userId: String(data.userId || ''),
                        userName: data.userName || 'Kullanıcı',
                        timestamp: Date.now(),
                    });
                });
            }
            catch (e) {
                console.warn('[Soundboard] Socket listener failed:', e);
            }
        }
    }
    getAudioContext() {
        if (!this.audioCtx || this.audioCtx.state === 'closed') {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            this.audioCtx = new AudioCtx();
        }
        if (this.audioCtx.state === 'suspended') {
            this.audioCtx.resume().catch(() => { });
        }
        return this.audioCtx;
    }
    getVolume() {
        return Math.round(this.volume * 100);
    }
    setVolume(volPercent) {
        this.volume = Math.max(0, Math.min(1, volPercent / 100));
        if (typeof window !== 'undefined') {
            localStorage.setItem('meci_soundboard_vol', String(volPercent));
        }
    }
    onReaction(listener) {
        this.reactionListeners.add(listener);
        return () => this.reactionListeners.delete(listener);
    }
    notifyReaction(event) {
        this.reactionListeners.forEach(cb => {
            try {
                cb(event);
            }
            catch { }
        });
    }
    getSounds() {
        return [...exports.DEFAULT_SOUNDS, ...this.customSounds];
    }
    loadCustomSounds() {
        try {
            const raw = localStorage.getItem('meci_custom_sounds');
            if (raw) {
                this.customSounds = JSON.parse(raw);
            }
        }
        catch { }
    }
    addCustomSound(name, emoji, dataUrl) {
        const item = {
            id: `custom_${Date.now()}`,
            name,
            emoji: emoji || '🎵',
            category: 'custom',
            customUrl: dataUrl,
        };
        this.customSounds.push(item);
        localStorage.setItem('meci_custom_sounds', JSON.stringify(this.customSounds));
    }
    deleteCustomSound(id) {
        this.customSounds = this.customSounds.filter(s => s.id !== id);
        localStorage.setItem('meci_custom_sounds', JSON.stringify(this.customSounds));
    }
    /**
     * Play a sound effect and broadcast to the current voice channel
     */
    play(sound) {
        const soundItem = typeof sound === 'string'
            ? this.getSounds().find(s => s.id === sound) || { id: sound, name: sound, emoji: '🔊', category: 'classics' }
            : sound;
        // 1. Play synthesized audio locally
        if (soundItem.customUrl) {
            this.playAudioUrl(soundItem.customUrl);
        }
        else {
            this.playSynthesizedSound(soundItem.id);
        }
        const state = useStore_1.useStore.getState();
        const channelId = state.voiceConnection.channelId;
        const currentUser = state.currentUser;
        const reaction = {
            soundId: soundItem.id,
            soundName: soundItem.name,
            emoji: soundItem.emoji,
            userId: String(currentUser?.id || 'me'),
            userName: currentUser?.displayName || currentUser?.username || 'Sen',
            timestamp: Date.now(),
        };
        // 2. Trigger local visual reaction immediately
        this.notifyReaction(reaction);
        // 3. Broadcast to all peers in voice channel
        if (channelId) {
            try {
                const socket = RealtimeService_1.realtimeService.getSocket();
                socket.emit('voice:soundboard', {
                    channelId,
                    soundId: soundItem.id,
                    soundName: soundItem.name,
                    emoji: soundItem.emoji,
                    userId: currentUser?.id,
                    userName: currentUser?.displayName || currentUser?.username || 'Kullanıcı',
                });
            }
            catch (e) {
                console.warn('[Soundboard] Failed to broadcast soundboard effect:', e);
            }
        }
    }
    playAudioUrl(url) {
        try {
            this.stopAudio();
            const audio = new Audio(url);
            audio.crossOrigin = 'anonymous';
            audio.volume = this.volume;
            this.currentAudioElement = audio;
            audio.play().catch(e => {
                console.warn('[Soundboard] Audio playback autoplay blocked or failed:', e);
            });
        }
        catch (e) {
            console.warn('[Soundboard] Custom sound playback failed:', e);
        }
    }
    stopAudio() {
        if (this.currentAudioElement) {
            this.currentAudioElement.pause();
            this.currentAudioElement.currentTime = 0;
            this.currentAudioElement = null;
        }
    }
    /**
     * Pure Web Audio API High-Fidelity Synthesizer for built-in SFX
     */
    playSynthesizedSound(soundId) {
        const ctx = this.getAudioContext();
        const now = ctx.currentTime;
        const masterGain = ctx.createGain();
        masterGain.gain.setValueAtTime(this.volume, now);
        masterGain.connect(ctx.destination);
        switch (soundId) {
            case 'airhorn': {
                // Multi-oscillator stadium airhorn
                const freqs = [466.16, 470.5, 622.25]; // Bb4, detuned, Eb5
                freqs.forEach(f => {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'sawtooth';
                    osc.frequency.setValueAtTime(f, now);
                    osc.frequency.exponentialRampToValueAtTime(f * 1.02, now + 0.1);
                    osc.frequency.exponentialRampToValueAtTime(f * 0.98, now + 0.9);
                    gain.gain.setValueAtTime(0.001, now);
                    gain.gain.linearRampToValueAtTime(0.25, now + 0.05);
                    gain.gain.exponentialRampToValueAtTime(0.18, now + 0.7);
                    gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.1);
                    osc.connect(gain);
                    gain.connect(masterGain);
                    osc.start(now);
                    osc.stop(now + 1.15);
                });
                break;
            }
            case 'badumtss': {
                // 1. Kick "Ba"
                const kickOsc = ctx.createOscillator();
                const kickGain = ctx.createGain();
                kickOsc.frequency.setValueAtTime(140, now);
                kickOsc.frequency.exponentialRampToValueAtTime(45, now + 0.15);
                kickGain.gain.setValueAtTime(0.6, now);
                kickGain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
                kickOsc.connect(kickGain);
                kickGain.connect(masterGain);
                kickOsc.start(now);
                kickOsc.stop(now + 0.2);
                // 2. Snare "Dum" at +0.22s
                const snareOsc = ctx.createOscillator();
                const snareGain = ctx.createGain();
                snareOsc.frequency.setValueAtTime(180, now + 0.22);
                snareOsc.frequency.exponentialRampToValueAtTime(70, now + 0.38);
                snareGain.gain.setValueAtTime(0.001, now);
                snareGain.gain.setValueAtTime(0.5, now + 0.22);
                snareGain.gain.exponentialRampToValueAtTime(0.001, now + 0.42);
                snareOsc.connect(snareGain);
                snareGain.connect(masterGain);
                snareOsc.start(now + 0.22);
                snareOsc.stop(now + 0.45);
                // 3. Crash Cymbal "Tsss" at +0.48s
                const crashBuffer = ctx.createBuffer(1, ctx.sampleRate * 0.9, ctx.sampleRate);
                const data = crashBuffer.getChannelData(0);
                for (let i = 0; i < data.length; i++)
                    data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.25));
                const crashSrc = ctx.createBufferSource();
                crashSrc.buffer = crashBuffer;
                const crashFilter = ctx.createBiquadFilter();
                crashFilter.type = 'highpass';
                crashFilter.frequency.setValueAtTime(5000, now + 0.48);
                const crashGain = ctx.createGain();
                crashGain.gain.setValueAtTime(0.4, now + 0.48);
                crashGain.gain.exponentialRampToValueAtTime(0.001, now + 1.35);
                crashSrc.connect(crashFilter);
                crashFilter.connect(crashGain);
                crashGain.connect(masterGain);
                crashSrc.start(now + 0.48);
                break;
            }
            case 'quack': {
                // Duck Quack (Bandpass modulated sweep)
                const osc = ctx.createOscillator();
                const filter = ctx.createBiquadFilter();
                const gain = ctx.createGain();
                osc.type = 'sawtooth';
                osc.frequency.setValueAtTime(340, now);
                osc.frequency.exponentialRampToValueAtTime(180, now + 0.45);
                filter.type = 'bandpass';
                filter.frequency.setValueAtTime(1200, now);
                filter.frequency.exponentialRampToValueAtTime(600, now + 0.45);
                filter.Q.setValueAtTime(4.0, now);
                gain.gain.setValueAtTime(0.01, now);
                gain.gain.linearRampToValueAtTime(0.4, now + 0.05);
                gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
                osc.connect(filter);
                filter.connect(gain);
                gain.connect(masterGain);
                osc.start(now);
                osc.stop(now + 0.55);
                break;
            }
            case 'bruh': {
                // Deep resonant sub-drop "Bruh"
                const osc = ctx.createOscillator();
                const filter = ctx.createBiquadFilter();
                const gain = ctx.createGain();
                osc.type = 'sawtooth';
                osc.frequency.setValueAtTime(130, now);
                osc.frequency.exponentialRampToValueAtTime(55, now + 0.7);
                filter.type = 'lowpass';
                filter.frequency.setValueAtTime(350, now);
                filter.frequency.exponentialRampToValueAtTime(120, now + 0.7);
                filter.Q.setValueAtTime(6.0, now);
                gain.gain.setValueAtTime(0.01, now);
                gain.gain.linearRampToValueAtTime(0.6, now + 0.08);
                gain.gain.exponentialRampToValueAtTime(0.001, now + 0.85);
                osc.connect(filter);
                filter.connect(gain);
                gain.connect(masterGain);
                osc.start(now);
                osc.stop(now + 0.9);
                break;
            }
            case 'gg': {
                // 8-bit retro victory arpeggio
                const notes = [261.63, 329.63, 392.00, 523.25, 659.25, 783.99]; // C4, E4, G4, C5, E5, G5
                notes.forEach((f, idx) => {
                    const t = now + idx * 0.11;
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'square';
                    osc.frequency.setValueAtTime(f, t);
                    gain.gain.setValueAtTime(0.001, t);
                    gain.gain.linearRampToValueAtTime(0.25, t + 0.02);
                    gain.gain.exponentialRampToValueAtTime(0.001, t + (idx === notes.length - 1 ? 0.45 : 0.12));
                    osc.connect(gain);
                    gain.connect(masterGain);
                    osc.start(t);
                    osc.stop(t + 0.5);
                });
                break;
            }
            case 'victory': {
                // Majestic Brass Fanfare Triad
                const chords = [
                    { time: 0, f: [349.23, 440.00, 523.25], dur: 0.3 }, // F, A, C
                    { time: 0.35, f: [392.00, 493.88, 587.33], dur: 0.3 }, // G, B, D
                    { time: 0.72, f: [523.25, 659.25, 783.99], dur: 1.0 }, // C, E, G (Majestic!)
                ];
                chords.forEach(chord => {
                    chord.f.forEach(freq => {
                        const osc = ctx.createOscillator();
                        const gain = ctx.createGain();
                        osc.type = 'sawtooth';
                        osc.frequency.setValueAtTime(freq, now + chord.time);
                        gain.gain.setValueAtTime(0.001, now + chord.time);
                        gain.gain.linearRampToValueAtTime(0.2, now + chord.time + 0.04);
                        gain.gain.exponentialRampToValueAtTime(0.001, now + chord.time + chord.dur);
                        osc.connect(gain);
                        gain.connect(masterGain);
                        osc.start(now + chord.time);
                        osc.stop(now + chord.time + chord.dur + 0.05);
                    });
                });
                break;
            }
            case 'tada': {
                // Ta-Daaa! Chime
                const fList = [523.25, 659.25, 783.99, 1046.50];
                fList.forEach((f, i) => {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'triangle';
                    osc.frequency.setValueAtTime(f, now + i * 0.06);
                    gain.gain.setValueAtTime(0.001, now + i * 0.06);
                    gain.gain.linearRampToValueAtTime(0.3, now + i * 0.06 + 0.03);
                    gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.2);
                    osc.connect(gain);
                    gain.connect(masterGain);
                    osc.start(now + i * 0.06);
                    osc.stop(now + 1.25);
                });
                break;
            }
            case 'applause': {
                // Audience Applause (Filtered White Noise Burst)
                const dur = 2.0;
                const buffer = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
                const data = buffer.getChannelData(0);
                for (let i = 0; i < data.length; i++) {
                    const envelope = Math.sin((i / data.length) * Math.PI);
                    data[i] = (Math.random() * 2 - 1) * envelope;
                }
                const src = ctx.createBufferSource();
                src.buffer = buffer;
                const filter = ctx.createBiquadFilter();
                filter.type = 'bandpass';
                filter.frequency.setValueAtTime(1400, now);
                filter.Q.setValueAtTime(1.5, now);
                const gain = ctx.createGain();
                gain.gain.setValueAtTime(0.35, now);
                src.connect(filter);
                filter.connect(gain);
                gain.connect(masterGain);
                src.start(now);
                break;
            }
            case 'boing': {
                // Cartoon spring "Boing"
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.setValueAtTime(220, now);
                osc.frequency.exponentialRampToValueAtTime(880, now + 0.18);
                osc.frequency.exponentialRampToValueAtTime(320, now + 0.55);
                gain.gain.setValueAtTime(0.01, now);
                gain.gain.linearRampToValueAtTime(0.5, now + 0.04);
                gain.gain.exponentialRampToValueAtTime(0.001, now + 0.7);
                osc.connect(gain);
                gain.connect(masterGain);
                osc.start(now);
                osc.stop(now + 0.75);
                break;
            }
            case 'discord_ping': {
                // Discord notification chime (2 crystal notes)
                const notes = [659.25, 783.99]; // E5, G5
                notes.forEach((f, idx) => {
                    const t = now + idx * 0.12;
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(f, t);
                    gain.gain.setValueAtTime(0.001, t);
                    gain.gain.linearRampToValueAtTime(0.35, t + 0.02);
                    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
                    osc.connect(gain);
                    gain.connect(masterGain);
                    osc.start(t);
                    osc.stop(t + 0.5);
                });
                break;
            }
            case 'fart': {
                // Hilarious comedic sound effect
                const osc = ctx.createOscillator();
                const filter = ctx.createBiquadFilter();
                const gain = ctx.createGain();
                osc.type = 'sawtooth';
                osc.frequency.setValueAtTime(95, now);
                osc.frequency.linearRampToValueAtTime(45, now + 0.6);
                filter.type = 'lowpass';
                filter.frequency.setValueAtTime(280, now);
                filter.Q.setValueAtTime(7.0, now);
                gain.gain.setValueAtTime(0.4, now);
                gain.gain.exponentialRampToValueAtTime(0.001, now + 0.75);
                osc.connect(filter);
                filter.connect(gain);
                gain.connect(masterGain);
                osc.start(now);
                osc.stop(now + 0.8);
                break;
            }
            default: {
                // Fallback gentle tone
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.setValueAtTime(520, now);
                gain.gain.setValueAtTime(0.2, now);
                gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
                osc.connect(gain);
                gain.connect(masterGain);
                osc.start(now);
                osc.stop(now + 0.4);
                break;
            }
        }
    }
}
exports.soundboardService = new SoundboardService();
